import React, { useState, useEffect, useContext } from "react";
import { API_BASE_URL } from "../../environment";
import { AuthContext } from "../../context/authcontext";
import ProductsServicesSelector from "../../feature-module/crm/leads/ProductsServicesSelector";
import { getAuthHeaders } from "../../utils/authUtils";
import { useTabVisibility } from "../../hooks/useTabVisibility";

interface ProductsServicesProps {
  tenant_id: string | null;
  related_entity_id?: number;
  related_entity_name: string;
  onActivityUpdate?: () => void;
  onEntityUpdate?: () => void;
  currencyCode?: string;
}

const ProductsServices: React.FC<ProductsServicesProps> = ({
  tenant_id,
  related_entity_id,
  related_entity_name,
  onActivityUpdate,
  onEntityUpdate,
  currencyCode,
}) => {
  const { selectedTenantId } = useContext(AuthContext);
  const [existingProductsServices, setExistingProductsServices] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tenantInfo, setTenantInfo] = useState<{
    is_service_tax?: boolean;
    is_gst_tax?: boolean;
    is_vat_tax?: boolean;
    service_tax_default_percentage?: number;
    gst_tax_default_percentage?: number;
    vat_tax_default_percentage?: number;
    proposal_min_discount_percentage?: number;
    proposal_max_discount_percentage?: number;
  } | undefined>(undefined);
  const [dataLoaded, setDataLoaded] = useState(false);

  const fetchTenantInfo = async () => {
    if (!tenant_id) return;
    
    try {
      const response = await fetch(
        `${API_BASE_URL}/tenant/${tenant_id}/`,
        {
          method: "GET",
          headers: getAuthHeaders(),
        }
      );

      if (!response.ok) return;

      const data = await response.json();
      setTenantInfo({
        is_service_tax: data.is_service_tax,
        is_gst_tax: data.is_gst_tax,
        is_vat_tax: data.is_vat_tax,
        service_tax_default_percentage: data.service_tax_default_percentage,
        gst_tax_default_percentage: data.gst_tax_default_percentage,
        vat_tax_default_percentage: data.vat_tax_default_percentage,
        proposal_min_discount_percentage: data.proposal_min_discount_percentage,
        proposal_max_discount_percentage: data.proposal_max_discount_percentage,
      });
    } catch (error) {
      console.error("Error fetching tenant info:", error);
    }
  };

  const fetchProductsServices = async () => {
    if (!related_entity_id || !tenant_id) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await fetch(
        `${API_BASE_URL}/lifecycle-product-n-service/?related_entity_name=${related_entity_name}&related_entity_id=${related_entity_id}`,
        {
          method: "GET",
          headers: getAuthHeaders(),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch products/services");
      }

      const data = await response.json();
      const services = Array.isArray(data.results) ? data.results : [];
      
      setExistingProductsServices(services);
    } catch (err: any) {
      console.error("Error fetching products/services:", err);
      setError("Failed to load products/services");
    } finally {
      setLoading(false);
    }
  };

  const { hasBeenActivated } = useTabVisibility({
    tabId: "products-services",
    onTabActivated: () => {
      if (!dataLoaded && tenant_id && related_entity_id) {
        fetchProductsServices();
        fetchTenantInfo();
        setDataLoaded(true);
      }
    },
  });

  useEffect(() => {
    if (hasBeenActivated && !dataLoaded && tenant_id && related_entity_id) {
      fetchProductsServices();
      fetchTenantInfo();
      setDataLoaded(true);
    }
  }, [hasBeenActivated, dataLoaded, related_entity_id, tenant_id, related_entity_name]);

  const handleRefreshData = () => {
    fetchProductsServices();
    if (onActivityUpdate) {
      onActivityUpdate();
    }
    if (onEntityUpdate) {
      onEntityUpdate();
      setTimeout(() => {
        onEntityUpdate();
      }, 1000);
    }
  };

  if (!hasBeenActivated) {
    return <div className="tab-pane fade" id="products-services" />;
  }

  if (loading) {
    return (
      <div className="tab-pane fade" id="products-services">
        <div className="contact-activity">
          <div className="badge-soft-secondary d-flex align-items-center mb-4">
            <div className="text-center py-4 w-100">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2 text-muted">Loading products & services...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="tab-pane fade" id="products-services">
        <div className="contact-activity">
          <div className="badge-soft-secondary d-flex align-items-center mb-4">
            <div className="text-center py-4 w-100">
              <div className="alert alert-danger">
                <i className="ti ti-alert-circle me-2"></i>
                {error}
                <button
                  className="btn btn-sm btn-outline-primary mt-2"
                  onClick={fetchProductsServices}
                >
                  <i className="ti ti-refresh me-1"></i>
                  Retry
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="tab-pane fade" id="products-services">
      <div className="contact-activity">
        <div className="activity-wrap">
        <div className="d-flex align-items-center smt-2" 
        style={{
          backgroundColor:'#fff',
          padding:'15px',
        }}>
          <h4 className="fw-semibold mb-0">Products & Services</h4>
        </div>
          <ProductsServicesSelector
            value={[]}
            onChange={() => {}}
            existingItems={existingProductsServices}
            isEditMode={true}
            entityId={related_entity_id}
            entityType= {related_entity_name}
            onRefreshData={handleRefreshData}
            isDetailPage={true}
            tenantInfo={tenantInfo}
            currencyCode={currencyCode}
          />
        </div>
      </div>
    </div>
  );
};

export default ProductsServices;
